import React, { useMemo } from 'react';
import { Target, CheckCircle2, Clock, Timer, PhoneForwarded } from 'lucide-react';
import { Loan } from './types';
interface KPITilesProps {
  loans: Loan[];
  claimedToday?: number;
}
export function KPITiles({
  loans,
  claimedToday = 0
}: KPITilesProps) {
  const stats = useMemo(() => {
    const total = loans.length;
    const over72 = loans.filter(l => l.timeSinceLastCall > 72).length;
    const dialReady = loans.filter(l => l.isDialReady).length;
    const avgHours = total > 0 ? loans.reduce((s, l) => s + l.timeSinceLastCall, 0) / total : 0;
    return {
      total,
      over72,
      dialReady,
      avgHours
    };
  }, [loans]);
  const fmtAvg = (h: number) => {
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };
  const kpis = [{
    label: 'Total Shark Tank Loans',
    value: stats.total.toLocaleString(),
    icon: Target,
    color: 'from-cyan-500/20 to-blue-600/20',
    iconColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/30',
    trend: 'Open pool'
  }, {
    label: 'Loans Claimed Today',
    value: String(claimedToday),
    icon: CheckCircle2,
    color: 'from-emerald-500/20 to-green-600/20',
    iconColor: 'text-emerald-400',
    borderColor: 'border-emerald-500/30',
    trend: 'Recovery rate'
  }, {
    label: 'Waiting > 72 Hours',
    value: stats.over72.toLocaleString(),
    icon: Clock,
    color: 'from-orange-500/20 to-red-600/20',
    iconColor: 'text-orange-400',
    borderColor: 'border-orange-500/30',
    trend: 'Critical priority',
    pulse: stats.over72 > 0
  }, {
    label: 'Avg Time Since Contact',
    value: fmtAvg(stats.avgHours),
    icon: Timer,
    color: 'from-purple-500/20 to-pink-600/20',
    iconColor: 'text-purple-400',
    borderColor: 'border-purple-500/30',
    trend: 'Across queue'
  }, {
    label: 'Dial Queue Ready',
    value: stats.dialReady.toLocaleString(),
    icon: PhoneForwarded,
    color: 'from-blue-500/20 to-indigo-600/20',
    iconColor: 'text-blue-400',
    borderColor: 'border-blue-500/30',
    trend: 'Available now'
  }];
  return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-id="element-139">
      {kpis.map((kpi, index) => <div key={index} className={`relative overflow-hidden rounded-2xl border ${kpi.borderColor} bg-gradient-to-br ${kpi.color} p-5 backdrop-blur-sm`} data-id="element-140">
          <div className="flex justify-between items-start mb-4" data-id="element-141">
            <div className={`p-2 rounded-lg bg-slate-950/50 ${kpi.pulse ? 'animate-pulse' : ''}`} data-id="element-142">
              <kpi.icon className={`w-5 h-5 ${kpi.iconColor}`} data-id="element-143" />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-950/40 px-2 py-1 rounded-full" data-id="element-144">
              {kpi.trend}
            </span>
          </div>
          <div data-id="element-145">
            <h3 className="text-3xl font-bold font-display text-white mb-1" data-id="element-146">
              {kpi.value}
            </h3>
            <p className="text-xs font-medium text-slate-300" data-id="element-147">{kpi.label}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10 pointer-events-none" data-id="element-148">
            <kpi.icon className="w-32 h-32" data-id="element-149" />
          </div>
        </div>)}
    </div>;
}